import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarIcon, Upload, FileText, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { es as esLocale } from "date-fns/locale/es";
import { useInView } from "../hooks/useInView";
import { toast } from "sonner";
import { LANGUAGES_ES, LANGUAGES_EN } from "../data/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/backend`;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export default function QuoteForm() {
    const { t, lang } = useLanguage();
    const [ref, inView] = useInView();
    const [formData, setFormData] = useState({
        source_language: "",
        target_language: "",
        service_type: "",
        email: "",
        phone: "",
        message: "",
    });
    const [rgpd, setRgpd] = useState({ communications: false, privacy: false });
    const [date, setDate] = useState(undefined);
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;
        setFileError("");
        if (selected.size > MAX_FILE_SIZE) {
            setFileError(t.fileValidation.tooLarge);
            setFile(null);
            e.target.value = "";
            return;
        }
        setFile(selected);
    };

    const updateField = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const [submitted, setSubmitted] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.source_language) newErrors.source_language = true;
        if (!formData.target_language) newErrors.target_language = true;
        if (!formData.service_type) newErrors.service_type = true;
        if (!formData.email) {
            newErrors.email = true;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = true;
        }
        if (!formData.phone.trim()) {
            newErrors.phone = true;
        }
        if (!rgpd.privacy) newErrors.privacy = true;
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        setSubmitted(false);

        const fd = new FormData();
        fd.append("source_language", formData.source_language);
        fd.append("target_language", formData.target_language);
        fd.append("service_type", formData.service_type);
        fd.append("deadline", date ? format(date, "yyyy-MM-dd") : "");
        fd.append("email", formData.email);
        fd.append("phone", formData.phone);
        fd.append("message", formData.message);
        fd.append("rgpd_privacy", rgpd.privacy);
        fd.append("rgpd_communications", rgpd.communications);
        if (file) fd.append("file", file);

        try {
            const response = await fetch(`${API}/q_8d2f1a.php`, {
                method: "POST",
                body: fd,
            });

            if (response.ok) {
                setSubmitted(true);
                toast.success(t.quote.success);
                setFormData({ source_language: "", target_language: "", service_type: "", email: "", phone: "", message: "" });
                setRgpd({ communications: false, privacy: false });
                setDate(undefined);
                setFile(null);
            } else {
                toast.error(t.quote.error);
            }
        } catch (err) {
            console.error("Submit error:", err);
            toast.error(t.quote.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="presupuesto"
            className="py-20 md:py-32 px-6 bg-slate-50 dark:bg-slate-900/50"
            ref={ref}
            data-testid="quote-section"
        >
            <div className={`max-w-4xl mx-auto ${inView ? "animate-fade-in-up" : "opacity-0"}`}>
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                        {t.quote.title}
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                        {t.quote.subtitle}
                    </p>
                </div>

                <div
                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 md:p-10 space-y-6 shadow-lg"
                    data-testid="quote-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t.quote.sourceLanguage} *</label>
                            <Select
                                onValueChange={(v) => { updateField("source_language", v); setErrors(prev => ({...prev, source_language: null})) }}
                                value={formData.source_language}
                            >
                                <SelectTrigger data-testid="source-language-select" className={`h-11 ${errors.source_language ? 'border-red-500' : ''}`}>
                                    <SelectValue placeholder={t.quote.sourceLanguage} />
                                </SelectTrigger>
                                <SelectContent>
                                    {(lang === "es" ? LANGUAGES_ES : LANGUAGES_EN).map((l) => (
                                        <SelectItem key={l} value={l}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t.quote.targetLanguage} *</label>
                            <Select
                                onValueChange={(v) => { updateField("target_language", v); setErrors(prev => ({...prev, target_language: null})) }}
                                value={formData.target_language}
                            >
                                <SelectTrigger data-testid="target-language-select" className={`h-11 ${errors.target_language ? 'border-red-500' : ''}`}>
                                    <SelectValue placeholder={t.quote.targetLanguage} />
                                </SelectTrigger>
                                <SelectContent>
                                    {(lang === "es" ? LANGUAGES_ES : LANGUAGES_EN).map((l) => (
                                        <SelectItem key={l} value={l}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t.quote.serviceType} *</label>
                            <Select
                                onValueChange={(v) => { updateField("service_type", v); setErrors(prev => ({...prev, service_type: null})) }}
                                value={formData.service_type}
                            >
                                <SelectTrigger data-testid="service-type-select" className={`h-11 ${errors.service_type ? 'border-red-500' : ''}`}>
                                    <SelectValue placeholder={t.quote.serviceType} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="sworn">{t.quote.sworn}</SelectItem>
                                    <SelectItem value="simple">{t.quote.simple}</SelectItem>
                                    <SelectItem value="interpretation">{t.quote.interpretation}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t.quote.deadline}</label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="w-full h-11 justify-start text-left font-normal"
                                        data-testid="deadline-picker"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                                        {date
                                            ? format(date, "PPP", { locale: lang === "es" ? esLocale : undefined })
                                            : t.quote.selectDate}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={(d) => d < new Date()}
                                        data-testid="calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t.quote.uploadFile}</label>
                        <div
                            className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors duration-200"
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="file-upload-area"
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleFileChange}
                                data-testid="file-input"
                            />
                            {file ? (
                                <div className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400">
                                    <FileText className="w-5 h-5" />
                                    <span className="font-medium">{file.name}</span>
                                    <span className="text-xs text-slate-400">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2 text-slate-400">
                                    <Upload className="w-8 h-8" />
                                    <span className="text-sm">{t.quote.dragDrop}</span>
                                    <span className="text-xs">{t.fileValidation.maxSize}</span>
                                </div>
                            )}
                        </div>
                        {fileError && (
                            <div className="flex items-center gap-2 text-red-500 text-sm mt-2" data-testid="file-error">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span>{fileError}</span>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className={`text-sm font-medium ${errors.email ? 'text-red-500' : ''}`}>{t.quote.email} *</label>
                            <Input
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => { updateField("email", e.target.value); setErrors(prev => ({...prev, email: null})) }}
                                placeholder={t.quote.email}
                                className={`h-11 ${errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                data-testid="email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={`text-sm font-medium ${errors.phone ? 'text-red-500' : ''}`}>{t.quote.phone} *</label>
                            <Input
                                type="tel"
                                required
                                value={formData.phone}
                                onChange={(e) => { updateField("phone", e.target.value); setErrors(prev => ({...prev, phone: null})) }}
                                placeholder={t.quote.phonePlaceholder}
                                className={`h-11 ${errors.phone ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                data-testid="phone-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t.quote.message}</label>
                        <Textarea
                            value={formData.message}
                            onChange={(e) => { updateField("message", e.target.value); }}
                            placeholder={t.quote.messagePlaceholder}
                            rows={4}
                            data-testid="message-textarea"
                        />
                    </div>

                    <div className="space-y-4 pt-2">
                        <div className={`flex items-start gap-3 text-sm ${errors.privacy ? 'text-red-500' : 'text-slate-600 dark:text-slate-400'}`}>
                            <input
                                type="checkbox"
                                id="q-privacy"
                                checked={rgpd.privacy}
                                onChange={(e) => { setRgpd(p => ({ ...p, privacy: e.target.checked })); setErrors(p => ({...p, privacy: null})) }}
                                className={`mt-1 flex-shrink-0 cursor-pointer ${errors.privacy ? 'ring-2 ring-red-500 border-red-500' : ''}`}
                            />
                            <label htmlFor="q-privacy" className="cursor-pointer">
                                {lang === "es" ? "He leído y acepto la " : "I have read and accept the "}
                                <Link
                                    to={lang === "es" ? "/politica-de-privacidad" : "/privacy-policy"}
                                    className="underline text-blue-600 dark:text-blue-400 hover:opacity-80 transition-opacity"
                                >
                                    {lang === "es" ? "política de privacidad" : "privacy policy"}
                                </Link>
                                {" *"}
                            </label>


                        </div>
                        <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-400">
                            <input
                                type="checkbox"
                                id="q-comms"
                                checked={rgpd.communications}
                                onChange={(e) => setRgpd(p => ({ ...p, communications: e.target.checked }))}
                                className="mt-1 flex-shrink-0 cursor-pointer"
                            />
                            <label htmlFor="q-comms" className="cursor-pointer">{t.rgpd.communications}</label>
                        </div>
                    </div>

                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-12 text-base font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/25"
                        data-testid="submit-quote-btn"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t.quote.submitting}
                            </span>
                        ) : (
                            t.quote.submit
                        )}
                    </Button>

                    {submitted && (
                        <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-700 dark:text-emerald-300" data-testid="quote-success-message">
                            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm font-medium">{t.quote.success}</p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
